import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from '../ui/BaseModal';
import { useRejectDocument } from '../../services/documents.service';

interface RejectDocumentModalProps {
  companyId: string;
}

export interface RejectDocumentModalRef {
  open: (documentId: string, documentName: string) => void;
}

export const RejectDocumentModal = forwardRef<RejectDocumentModalRef, RejectDocumentModalProps>(
  ({ companyId }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [documentName, setDocumentName] = useState('');
    const [reason, setReason] = useState('');

    const rejectDocumentMutation = useRejectDocument(companyId);

    useImperativeHandle(ref, () => ({
      open: (docId: string, docName: string) => {
        setDocumentId(docId);
        setDocumentName(docName);
        setReason('');
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (!isOpen) {
        setReason('');
        setDocumentId(null);
        setDocumentName('');
      }
    }, [isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!documentId || !reason.trim()) return;

      rejectDocumentMutation.mutate(
        { documentId, reason: reason.trim() },
        { onSuccess: handleClose },
      );
    };

    const handleClose = () => {
      setIsOpen(false);
    };

    const isLoading = rejectDocumentMutation.isPending;
    const canSubmit = reason.trim().length > 0 && !isLoading;

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
          disabled={isLoading}
        >
          {t('documents.reject_document_modal.cancel')}
        </button>
        <button
          type="submit"
          form="reject-document-form"
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition disabled:opacity-50"
          disabled={!canSubmit}
        >
          {t('documents.reject_document_modal.submit')}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('documents.reject_document_modal.title')}
        footer={footer}
        maxWidth="md"
      >
        <form id="reject-document-form" onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('documents.reject_document_modal.document_name')}
            </label>
            <p className="text-gray-900 font-medium">{documentName}</p>
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">{t('documents.reject_document_modal.warning')}</p>
          </div>

          <div>
            <label
              htmlFor="rejection-reason"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t('documents.reject_document_modal.reason_label')}
            </label>
            <textarea
              id="rejection-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('documents.reject_document_modal.reason_placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={4}
              maxLength={1000}
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('documents.reject_document_modal.reason_hint', {
                count: reason.length,
                max: 1000,
              })}
            </p>
          </div>
        </form>
      </BaseModal>
    );
  },
);

RejectDocumentModal.displayName = 'RejectDocumentModal';
